import React, { useMemo, useState, useEffect } from 'react';
/* Local DailyData type to avoid relying on an unavailable named export.
   Matches the shape used by this component (optional date/timestamp fields,
   trades count and pnl value). */
interface DailyData {
  date?: string | number;
  t?: string | number;
  timestamp?: string | number;
  trades?: number;
  pnl?: number;
}
import { fmtMoneyWithDecimals } from '../../utils/formatters';
import type { TimeRange } from '../../types/dashboard';
import {
  getDashboardWidgetTimeframe,
  getWidgetTimeframeTrailingDays,
  type WidgetTimeframeToken,
} from '../../lib/dashboardTimeRange';

interface AverageDailyGainLossWidgetProps {
  dailyData: DailyData[];
  timeRange?: TimeRange;
}

export const AverageDailyGainLossWidget: React.FC<AverageDailyGainLossWidgetProps> = ({ dailyData, timeRange }) => {
  const [timeframe, setTimeframe] = useState<WidgetTimeframeToken>('7D');

  useEffect(() => {
    if (timeRange) setTimeframe(getDashboardWidgetTimeframe(timeRange));
  }, [timeRange]);

  const { avgDailyPnl, activeTradingDays } = useMemo(() => {
    const now = Date.now();
    const tfDays = getWidgetTimeframeTrailingDays(timeframe);
    const tfMs = tfDays * 24 * 60 * 60 * 1000;
    const startOfYearMs = (() => {
      const n = new Date();
      return new Date(n.getFullYear(), 0, 1, 0, 0, 0, 0).getTime();
    })();

    const hasDate = (dailyData || []).some(d => (d as any).date || (d as any).t || (d as any).timestamp);
    let filtered = dailyData || [];
    if (hasDate) {
      filtered = (dailyData || []).filter(d => {
        const raw = (d as any).date ?? (d as any).t ?? (d as any).timestamp;
        const ts = typeof raw === 'number' ? raw : (typeof raw === 'string' ? Date.parse(raw) : NaN);
        if (!Number.isFinite(ts)) return false;
        if (timeframe === 'YTD') return ts >= startOfYearMs;
        return ts >= (now - tfMs);
      });
    } else {
      if (timeframe === 'YTD') {
        const max = Math.min((dailyData || []).length, 365);
        filtered = (dailyData || []).slice(-max);
      } else {
        const count = Math.min((dailyData || []).length, tfDays);
        filtered = (dailyData || []).slice(-count);
      }
    }

    const tradingDays = filtered.filter(d => (d.trades || 0) > 0);
    const totalPnl = tradingDays.reduce((acc, d) => acc + (d.pnl || 0), 0);
    const avg = tradingDays.length === 0 ? 0 : totalPnl / tradingDays.length;
    return { avgDailyPnl: avg, activeTradingDays: tradingDays.length };
  }, [dailyData, timeframe]);

  const isGain = avgDailyPnl >= 0;
  const usesAnnualizedRunRate = timeframe === '30D' || timeframe === '12M' || timeframe === 'YTD';
  const annualizedEstimateUnavailable = usesAnnualizedRunRate && activeTradingDays < 20;

  const footerLabel = timeframe === '24H'
    ? 'End of Week Projection'
    : timeframe === '7D'
    ? 'End of Month Projection'
    : 'Annualized Run Rate';

  // Projection multipliers (use trading-day approximations)
  // 24H -> weekly projection (5 trading days)
  // 7D  -> monthly projection (~21 trading days)
  // 30D -> yearly projection (~252 trading days)
  // YTD -> next-year forecast (~252 trading days)
  const projectionMultiplier = timeframe === '24H' ? 5 : timeframe === '7D' ? 21 : 252;
  const projectedValue = avgDailyPnl * projectionMultiplier;

  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as any;
        if (!detail || detail.id !== 'averageDailyGainLoss') return;
        const val = detail.value as WidgetTimeframeToken | string;
        if (val === '24H' || val === '7D' || val === '30D' || val === '12M' || val === 'YTD') setTimeframe(val);
      } catch {}
    };
    window.addEventListener('widget-timeframe:selected', handler as EventListener);
    return () => window.removeEventListener('widget-timeframe:selected', handler as EventListener);
  }, []);

  return (
    <div className="h-full w-full pl-0 pr-4 pt-0 pb-0 flex flex-col text-center">
      {/* Middle: amount */}
      <div
        data-testid="average-daily-gain-loss-amount"
        className={`self-center -mt-1 text-3xl sm:text-4xl lg:text-5xl font-bold ${isGain ? 'text-emerald-600' : 'text-red-500'}`}
      >
        {fmtMoneyWithDecimals(avgDailyPnl, 0)}
      </div>

      <div
        data-testid="average-daily-gain-loss-active-days"
        className="self-center mt-1 text-xs text-zinc-500 dark:text-zinc-400"
      >
        Active trading days: {activeTradingDays}
      </div>

      {/* Bottom: run rate or estimate (use margin instead of fixed height spacer) */}
      <div
        data-testid="average-daily-gain-loss-projection"
        title={usesAnnualizedRunRate && !annualizedEstimateUnavailable
          ? 'Simple extrapolation of observed average daily P&L across 252 trading sessions. This is not a prediction.'
          : undefined}
        className="self-center mt-0 pt-[10px] text-sm text-zinc-600 dark:text-zinc-400"
      >
        {annualizedEstimateUnavailable ? (
          <span>More trading history needed for annualized estimates.</span>
        ) : (
          <>
            <span>{footerLabel}:</span>
            {' '}
            <span className={`ml-2 font-medium ${projectedValue >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {fmtMoneyWithDecimals(projectedValue, 0)}
            </span>
          </>
        )}
      </div>
    </div>
  );
};
